import { useEffect, useRef } from 'react';
import type { SheetRow } from '@/lib/api';
import { columnLetter } from '@/lib/columnMapping';
import { cn } from '@/lib/utils';

/**
 * The sheet, in view while a decision is made (ruled 2026-09-30, 4.10): the
 * header row and the first data rows, scrollable sideways, with the columns
 * the decision concerns highlighted and scrolled into view. A person never
 * decides about a column they cannot see.
 */
export function SheetPanel({
  columns,
  rows,
  highlight,
  rowsShown = 2,
}: {
  columns: string[];
  rows: SheetRow[];
  /** The columns the current decision concerns. */
  highlight: ReadonlySet<string>;
  rowsShown?: number;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const first = [...highlight][0];
  useEffect(() => {
    if (!first || !scroller.current) return;
    const cell = scroller.current.querySelector<HTMLElement>(`[data-column="${CSS.escape(first)}"]`);
    // jsdom draws nothing and has no scrollIntoView.
    if (cell && typeof cell.scrollIntoView === 'function') cell.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [first]);
  const shown = rows.slice(0, rowsShown);
  return (
    <div
      ref={scroller}
      role="region"
      aria-label="The sheet"
      className="sticky top-0 z-10 overflow-x-auto rounded-md border border-border bg-card"
    >
      <table className="tres-table w-max min-w-full text-xs [&_td]:px-2 [&_td]:py-1 [&_th]:px-2 [&_th]:py-1">
        <thead>
          <tr>
            <th scope="col" className="sticky left-0 bg-muted px-2 py-1 text-left font-medium text-muted-foreground">
              Row
            </th>
            {columns.map((c, i) => (
              <th
                key={c}
                scope="col"
                data-column={c}
                className={cn(
                  'whitespace-nowrap border-l border-border px-2 py-1 text-left font-mono font-medium',
                  highlight.has(c) ? 'bg-tres-action/15 text-tres-action' : 'bg-muted text-tres-text-primary',
                )}
              >
                <span className="mr-1 font-sans text-muted-foreground">{columnLetter(i)}</span>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((r, n) => (
            <tr key={n}>
              <th scope="row" className="sticky left-0 bg-muted px-2 py-1 text-left font-medium text-muted-foreground">
                {n + 1}
              </th>
              {columns.map((c) => (
                <td
                  key={c}
                  className={cn(
                    'max-w-48 truncate whitespace-nowrap border-l border-t border-border px-2 py-1',
                    highlight.has(c) && 'bg-tres-action/10',
                  )}
                  title={r[c] ?? ''}
                >
                  {r[c] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
